<template>
  <div id="mCalMgmtBox">

    <!-- ── 搜尋列（可收合） ───────────────────────────────────── -->
    <div class="m-search-bar">
      <div class="m-search-toggle" @click="searchExpanded = !searchExpanded">
        <span><i class="el-icon-search"></i> 查詢條件</span>
        <i :class="searchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
      </div>
      <el-collapse-transition>
        <div v-show="searchExpanded" class="m-search-body">
          <div v-if="divBtnVisible" class="m-form-row">
            <label>區處部門</label>
            <DivSelection
              v-model="selectedDiv"
              :noDivision="true"
              :filterDivisionCode="filterDivisionCode"
              @branch-selected="handleDivChange"
            />
          </div>
          <div class="m-form-row">
            <label>業務人員</label>
            <el-select v-model="selSales" placeholder="請選擇" no-data-text="無數據" style="width: 100%;">
              <el-option label="全部" value="全部"></el-option>
              <el-option
                :label="item.UserName"
                :value="item.UserID"
                v-for="item in branchStaffList"
                :key="item.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="m-form-row">
            <label>年月</label>
            <el-date-picker
              v-model="selyyyyMM"
              type="month"
              format="yyyy-MM"
              value-format="yyyy-MM"
              placeholder="選擇年月"
              @change="changeYYYYMM"
              style="width: 100%;"
            ></el-date-picker>
          </div>
          <el-button
            type="primary"
            icon="el-icon-search"
            style="width: 100%; margin-top: 8px;"
            @click="doSearch"
          >查詢</el-button>
        </div>
      </el-collapse-transition>
    </div>

    <!-- ── 圖例（橫向捲動） ───────────────────────────────────── -->
    <div v-if="salesList.length > 0" class="m-legend-scroll">
      <div
        v-for="(item, index) in salesList"
        :key="item.empID"
        class="m-legend-item"
        :class="getColorClass(index)"
      >{{ item.empName }}</div>
    </div>

    <!-- ── 行事曆 ─────────────────────────────────────────────── -->
    <div class="m-calendar-wrap">
      <el-calendar v-model="yyyyMM">
        <template slot="dateCell" slot-scope="{data}">
          <div class="m-cd" @click.stop="handleDayClick(data)">
            <div class="m-date-num" :class="{ 'is-selected': data.isSelected }">
              {{ data.day.split('-').slice(2).join('') }}
            </div>
            <div class="m-event-wrap">
              <template v-if="getDateEvents(data.day).length > 0">
                <div
                  v-for="(group, colorClass) in groupEventsByColor(data.day)"
                  :key="colorClass"
                  class="m-ev-group"
                  :class="colorClass"
                >
                  <span
                    v-for="(ev, idx) in group.slice(0, 2)"
                    :key="idx"
                    class="m-ev-label"
                  >{{ ev.source === 'case' ? getCaseTimeTag(ev.startTime) + truncateText(ev.target, 6) : truncateText(ev.title, 6) }}
                </span>
                </div>
                <div v-if="getDateEvents(data.day).length > 3" class="m-more-badge">
                  +{{ getDateEvents(data.day).length - 3 }}
                </div>
              </template>
            </div>
          </div>
        </template>
      </el-calendar>
    </div>

    <!-- ── FAB 新增按鈕 ──────────────────────────────────────── -->
    <div v-if="btnCalendarEditable" class="m-fab" @click="openAddDrawer(null)">
      <i class="el-icon-plus"></i>
    </div>

    <!-- ── 當日事件列表抽屜 ───────────────────────────────────── -->
    <el-drawer
      :title="`${selectedDate || ''} 行程`"
      :visible.sync="editDrawerVisible"
      direction="rtl"
      size="30%"
      :with-header="true"
    >
      <div class="m-drawer-content">
        <div v-if="dayEvents.length === 0" class="m-no-events">當天沒有行程</div>
        <div v-else class="m-events-list">
          <div
            v-for="(event, index) in dayEvents"
            :key="index"
            class="m-event-item"
            :class="getSalesColor(event.sales, event.date)"
          >
            <div class="m-event-body">
              <div class="m-event-title">
                {{ event.source === 'case' ? '[案件] ' + event.target : event.title }}
              </div>
              <div class="m-event-meta">
                {{ event.sales }}
                <span v-if="event.startTime && event.endTime">
                  · {{ event.startTime }}–{{ event.endTime }}
                </span>
              </div>
              <div v-if="event.Memo" class="m-event-memo">{{ event.Memo }}</div>
              <div v-if="event.caseSource || event.status" class="m-event-tags">
                <el-tag v-if="event.caseSource" size="mini">{{ event.caseSource }}</el-tag>
                <el-tag v-if="event.status" size="mini" type="info">{{ event.status }}</el-tag>
              </div>
            </div>
            <div class="m-event-btns">
              <el-button
                type="primary"
                icon="el-icon-edit"
                size="mini"
                :disabled="!canEditEvent(event)"
                @click="editCaseOrSchedule(event, index)"
              ></el-button>
              <el-button
                v-if="event.source === 'calendar'"
                type="danger"
                icon="el-icon-delete"
                size="mini"
                :disabled="!canEditEvent(event)"
                @click="confirmDeleteEvent(index)"
              ></el-button>
              <div v-if="!canEditEvent(event)" class="m-lock-hint">
                <i class="el-icon-lock"></i>
                <span>僅可編輯本人行程</span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="btnCalendarEditable" class="m-drawer-add-btn">
          <el-button
            type="primary"
            icon="el-icon-plus"
            style="width: 100%;"
            @click="openAddForDate(selectedDate)"
          >新增此日行程</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- ── 新增 / 編輯行程抽屜 ──────────────────────────────── -->
    <el-drawer
      :title="currentEditIndex !== -1 ? '編輯行程' : '新增行程'"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="35%"
      :with-header="true"
    >
      <div class="m-drawer-content">
        <div class="m-form-item">
          <label>日期 <span class="req">*</span></label>
          <el-date-picker
            v-model="scheduleForm.date"
            type="date"
            placeholder="選擇日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            style="width: 100%;"
          ></el-date-picker>
        </div>
        <div class="m-form-item">
          <label>開始時間 <span class="req">*</span></label>
          <el-time-picker
            v-model="scheduleForm.startTime"
            placeholder="開始時間"
            format="HH:mm"
            value-format="HH:mm"
            :picker-options="{ selectableRange: '00:00:00 - 23:59:59' }"
            style="width: 100%;"
          ></el-time-picker>
        </div>
        <div class="m-form-item">
          <label>結束時間 <span class="req">*</span></label>
          <el-time-picker
            v-model="scheduleForm.endTime"
            placeholder="結束時間"
            format="HH:mm"
            value-format="HH:mm"
            :picker-options="{ selectableRange: '00:00:00 - 23:59:59' }"
            style="width: 100%;"
          ></el-time-picker>
        </div>
        <div class="m-form-item">
          <label>事由 <span class="req">*</span></label>
          <el-input placeholder="請輸入事由" 
          v-model="scheduleForm.target"
          :maxlength="100"></el-input>
        </div>
        <div class="m-form-item">
          <label>備註</label>
          <el-input
            type="textarea"
            :rows="3"
            placeholder="請輸入備註"
            v-model="scheduleForm.Memo"
            :maxlength="100"
          ></el-input>
        </div>
        <div class="m-drawer-footer">
          <el-button style="flex: 1;" @click="drawerVisible = false">取消</el-button>
          <el-button type="primary" style="flex: 2;" @click="saveSchedule">儲存</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- ── 案件編輯 dialog ────────────────────────────────────── -->
    <el-dialog title="案件編輯" :visible.sync="showCaseEdit" width="95%" center>
      <div class="m-dialog-box">
        <div class="m-detail-row">
          <span class="m-detail-label">案件編號</span>
          <span class="m-detail-value">{{ caseDetails[0] && caseDetails[0].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">到期日/限辦日期</span>
          <span class="m-detail-value">{{ caseDetails[1] && caseDetails[1].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">統一編號</span>
          <span class="m-detail-value">{{ caseDetails[2] && caseDetails[2].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">公司名稱</span>
          <span class="m-detail-value">{{ caseDetails[3] && caseDetails[3].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">資料來源</span>
          <span class="m-detail-value">{{ caseDetails[4] && caseDetails[4].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">專案名稱</span>
          <span class="m-detail-value">{{ caseDetails[5] && caseDetails[5].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">案件狀態</span>
          <div class="m-detail-value">
            <el-select v-model="caseDetails[6].value" placeholder="請選擇案件狀態" @change="checkReason" style="width: 100%;">
              <el-option v-for="(opt, oi) in nextSelectList" :key="oi" :value="opt"></el-option>
            </el-select>
            <el-select
              v-if="showReason"
              v-model="caseDetails[14].value"
              placeholder="請選擇原因"
              style="width: 100%; margin-top: 8px;"
            >
              <el-option v-for="(opt, oi) in selectedReason" :key="oi" :label="opt" :value="opt"></el-option>
            </el-select>
          </div>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">預計訪視時間</span>
          <div class="m-detail-value">
            <el-date-picker
              v-if="caseDetails[7]"
              v-model="caseDetails[7].value"
              type="date"
              size="small"
              value-format="yyyy-MM-dd"
              style="width: 100%;"
            ></el-date-picker>
          </div>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">區處</span>
          <span class="m-detail-value">{{ caseDetails[8] && caseDetails[8].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">分行名稱</span>
          <span class="m-detail-value">{{ caseDetails[9] && caseDetails[9].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">覆核主管</span>
          <span class="m-detail-value">{{ caseDetails[10] && caseDetails[10].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">承辦人</span>
          <span class="m-detail-value">{{ caseDetails[11] && caseDetails[11].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">建立時間</span>
          <span class="m-detail-value">{{ caseDetails[12] && caseDetails[12].value }}</span>
        </div>
        <div class="m-detail-row">
          <span class="m-detail-label">備註</span>
          <div class="m-detail-value">
            <el-input
              v-if="caseDetails[13]"
              v-model="caseDetails[13].value"
              type="textarea"
              rows="2"
              size="small"
              style="width: 100%;"
            ></el-input>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showCaseEdit = false">關閉</el-button>
        <el-button type="primary" @click="btnSaveDetail()">儲存</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import DivSelection from '../../../components/DivSelection.vue';
import { notify } from '../../../utils/notify';

export default {
  name: 'MobileCalMgmt',
  components: { DivSelection },

  data() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const currentMonthFirstDay = `${year}-${month.toString().padStart(2, '0')}-01`;

    return {
      // ── UI 狀態 ─────────────────────────────────────────────
      searchExpanded: false,

      // ── 同桌面版 data ────────────────────────────────────────
      btnCalendarEditable: true,
      divBtnVisible: false,
      selectedDiv: [],

      listQuery: {
        DivisionCode: '',
        DeptID: '',
        DateStart: '',
        DateEnd: '',
      },

      showCaseEdit: false,
      selectedEvent: {},
      selectedCase: {},
      caseDetails: [
        { title: '案件編號',       value: '' },
        { title: '到期日/限辦日期', value: '' },
        { title: '統一編號',       value: '' },
        { title: '公司名稱',       value: '' },
        { title: '資料來源',       value: '' },
        { title: '專案名稱',       value: '' },
        { title: '案件狀態',       value: '' },
        { title: '預計訪視時間',   value: '' },
        { title: '區處',          value: '' },
        { title: '分行名稱',       value: '' },
        { title: '覆核主管',       value: '' },
        { title: '承辦人',         value: '' },
        { title: '建立時間',       value: '' },
        { title: '備註',          value: '' },
        { title: '原因',          value: '' },
      ],

      yyyyMM: new Date(),
      selyyyyMM: currentMonthFirstDay,
      filterDivisionCode: '',

      selSales: '全部',

      drawerVisible: false,
      editDrawerVisible: false,
      selectedDate: null,
      currentEditIndex: -1,
      dayEvents: [],
      scheduleForm: {
        date: null,
        sales: '',
        target: '',
        status: '',
        Memo: '',
        startTime: '',
        endTime: '',
      },

      branchStaffList: [],
      calendarData: [],
      salesList: [],

      ReasonList: [
        { '客戶拒絕': ['對產品/服務不感興趣', '要求特定時間再聯繫', '暫無資金需求', '已有往來行庫', '其他'] },
        { '其他':    ['重覆名單', '聯絡方式錯誤', '聯絡不上', '停歇業', '其他'] },
        { '撤件':    ['訪談後無意願', '要求特定時間再聯繫', '其他'] },
      ],
      status_transion: [
        { '待安排訪視': ['客戶拒絕', '其他', '已安排訪視'] },
        { '已安排訪視': ['已訪視'] },
        { '已訪視':    ['撤件', '續行申貸', '已安排訪視'] },
      ],
      nextSelectList: [],
      showReason: false,
      selectedReason: [],
    };
  },

  methods: {

      getCaseTimeTag(startTime) {
          if (!startTime) return '[上午]';

          const text = String(startTime).trim();
          const match = text.match(/(\d{1,2}):(\d{2})/);
          if (!match) return '[上午]';

          const hour = parseInt(match[1], 10);
          if (Number.isNaN(hour)) return '[上午]';

          return hour < 12 ? '[上午]' : '[下午]';
      },

      truncateText(text, maxLength = 6) {
          if (text === null || text === undefined) return '';
          const value = String(text);
          return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
      },

    // ── 行事曆格子點擊：有事件 → 列表抽屜；無事件可新增 → 新增抽屜 ──
    handleDayClick(data) {
      const events = this.getDateEvents(data.day);
      this.selectedDate = data.day;
      if (events.length > 0) {
        this.dayEvents = events;
        this.editDrawerVisible = true;
      } else if (this.btnCalendarEditable) {
        this.openAddForDate(data.day);
      }
    },

    // ── 開啟「新增行程」抽屜（不帶預設日期） ──────────────────
    openAddDrawer(date) {
      this.currentEditIndex = -1;
      this.scheduleForm = {
        date: date || null,
        sales: '',
        target: '',
        status: '',
        Memo: '',
        startTime: '10:00',
        endTime: '12:00',
      };
      this.drawerVisible = true;
    },

    // ── 開啟「新增行程」抽屜（帶日期） ───────────────────────
    openAddForDate(dateStr) {
      this.currentEditIndex = -1;
      this.scheduleForm = {
        date: dateStr,
        sales: '',
        target: '',
        status: '',
        Memo: '',
        startTime: '10:00',
        endTime: '12:00',
      };
      this.editDrawerVisible = false;
      this.drawerVisible = true;
    },

    // ── 截短標題顯示 ──────────────────────────────────────────
    shortTitle(title) {
      if (!title) return '';
      return title.length > 4 ? title.slice(0, 4) + '…' : title;
    },

    // ── 以下邏輯與桌面版相同 ──────────────────────────────────

    handleDivChange(val) {
      this.listQuery.DivisionCode = val.DivisionCode;
      this.listQuery.DeptID = val.DeptID;
      this.getList();
      this.btnCalendarEditable = !this.selectedDiv;
      const deptID = this.listQuery.DeptID;
      this.getBranchStaff(deptID);
    },

    canEditEvent(event) {
      const userInfo = this.$store.getters.userInfo;
      if (!userInfo || !userInfo.UserName) {
        return false;
      }
      return event.sales === userInfo.UserName;
    },

    checkReason() {
      this.showReason = false;
      this.selectedReason = [];
      for (let i = 0; i < this.ReasonList.length; i++) {
        const key = Object.keys(this.ReasonList[i])[0];
        if (this.caseDetails[6] && key === this.caseDetails[6].value) {
          this.showReason = true;
          this.selectedReason = this.ReasonList[i][key];
          break;
        }
      }
      if (!this.showReason && this.caseDetails[14]) {
        this.caseDetails[14].value = '';
      }
    },

    async getCaseDetail(row) {
      const qry = { ID: row.ID };
      await this.$api.apicase.case_detail(qry).then(response => {
        row = response.data;
        this.selectedCase = row;
        this.caseDetails = [
          { title: '案件編號',       value: row.CaseNo },
          { title: '到期日/限辨期限', value: row.DueDt },
          { title: '統一編號',       value: row.UniComNm },
          { title: '公司名稱',       value: row.ComName },
          { title: '資料來源',       value: row.Source },
          { title: '專案名稱',       value: row.ProjectName },
          { title: '案件狀態',       value: row.CaseStatus },
          { title: '預計訪視時間',   value: row.ExpectVisitDt },
          { title: '區處',          value: row.DivisionName },
          { title: '分行名稱',       value: row.DeptName },
          { title: '覆核主管',       value: row.ApproveUserName },
          { title: '承辦人',         value: row.AssigneeUserName },
          { title: '建立時間',       value: row.CreateDt },
          { title: '備註',          value: row.Memo },
          { title: '原因',          value: '' },
        ];
        this.nextSelectList = [];
        for (let i = 0; i < this.status_transion.length; i++) {
          const key = Object.keys(this.status_transion[i])[0];
          if (key === row.CaseStatus) {
            this.nextSelectList = this.status_transion[i][key];
            break;
          }
        }
        this.showCaseEdit = true;
      }).catch(() => {
        notify({ title: '錯誤', message: '獲取案件詳細資訊失敗，請稍後再試', type: 'error' });
      });
    },

    btnSaveDetail() {
      if (this.showReason && (!this.caseDetails[14].value || this.caseDetails[14].value === '')) {
        notify({ title: '錯誤', message: '請選擇變更原因', type: 'error' });
        return;
      }
      if (this.caseDetails[6].value === '已安排訪視' && (!this.caseDetails[7].value || this.caseDetails[7].value === '')) {
        notify({ title: '錯誤', message: '當案件狀態為已安排訪視時，預計訪視時間不能為空', type: 'error' });
        return;
      }
      if (this.caseDetails[7].value && (!this.caseDetails[6].value || this.caseDetails[6].value === '')) {
        notify({ title: '錯誤', message: '當預計訪視時間有設定時，案件狀態不能為空', type: 'error' });
        return;
      }
      const qry = {
        ID: this.selectedCase.ID,
        Case_Status: this.caseDetails[6].value,
        Expect_Visit_Dt: this.caseDetails[7].value,
        Memo: this.caseDetails[13].value,
      };
      this.$api.apicase.case_update(qry).then(() => {
        this.getList();
        notify({ title: '成功', message: '案件明細已更新', type: 'success' });
      }).catch(() => {
        notify({ title: '錯誤', message: '更新案件明細失敗，請稍後再試', type: 'error' });
      });
      if (this.showReason) {
        const qry2 = { ID: this.selectedCase.ID, Case_Reason: this.caseDetails[14].value };
        this.$api.apicase.case_submit_approval(qry2).catch(() => {
          notify({ title: '錯誤', message: '更新案件變更原因失敗，請稍後再試', type: 'error' });
        });
      }
      this.showCaseEdit = false;
    },

    getColorClass(index) {
      const colors = ['gbcGreen', 'gbcOrange', 'gbcBlue', 'gbcRed', 'gbcPurple'];
      return colors[index % colors.length];
    },

    changeYYYYMM(item) {
      this.yyyyMM = item;
      this.updateEventColors();
    },

    async calClick(item) {
      if (item && item.date) {
        this.selectedDate = item.date;
        this.dayEvents = this.getDateEvents(item.date);
        this.editDrawerVisible = true;
      }
    },

    async editCaseOrSchedule(event, index) {
      if (this.dayEvents[index].source === 'case') {
        this.$confirm('此為案件行程，是否要轉到案件管理頁面進行編輯？', '提示', {
          confirmButtonText: '確定',
          cancelButtonText: '取消',
          type: 'info',
        }).then(() => {
          const caseId = this.dayEvents[index].caseId || this.dayEvents[index].CaseID || this.dayEvents[index].ID;
          if (!caseId) {
            notify({ title: '錯誤', message: '找不到案件編號，無法前往案件管理', type: 'error' });
            return;
          }
          this.$router.push({
            name: 'm-case-mgmt',
            query: { caseId },
          });
        }).catch(() => {
          this.$message({
            type: 'info',
            message: '已取消編輯',
          });
        });
      } else {
        this.openEditEvent(event, index);
      }
    },

    openEditEvent(event, index) {
      this.currentEditIndex = index;
      this.scheduleForm = {
        date: event.date,
        sales: event.sales,
        target: event.title || event.target || '',
        status: event.status,
        Memo: event.memo || '',
        startTime: event.startTime || '',
        endTime: event.endTime || '',
        id: event.id || event.ID,
      };
      this.editDrawerVisible = false;
      this.$nextTick(() => {
        this.drawerVisible = true;
      });
    },

    confirmDeleteEvent(index) {
      this.$confirm('確定要刪除該行程嗎？', '提示', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.deleteEvent(index);
      }).catch(() => {});
    },

    deleteEvent(index) {
      const event = this.dayEvents[index];
      this.$api.apicalendar.calendar_delete({ ID: event.id }).then(res => {
        if (res.statuscode == 200) {
          notify({ type: 'success', message: '刪除行程成功' });
          this.getList();
          this.editDrawerVisible = false;
        } else {
          notify({ type: 'error', message: '刪除行程失敗，請稍後再試' });
        }
      }).catch(() => {
        notify({ type: 'error', message: '刪除行程失敗，請稍後再試' });
      });
    },

    getDateEvents(date) {
      return this.calendarData.filter(event => event.date === date) || [];
    },

    groupEventsByColor(date) {
      const events = this.getVisibleEvents(date);
      const groups = {};
      events.forEach(event => {
        const colorClass = this.getSalesColorClass(event.sales, event.date);
        if (!groups[colorClass]) groups[colorClass] = [];
        groups[colorClass].push(event);
      });
      return groups;
    },

    getSalesColor(salesname) {
      const salesIndex = this.salesList.findIndex(s => s.empName === salesname);
      const map = { 0: 'gbcGreen', 1: 'gbcOrange', 2: 'gbcBlue', 3: 'gbcRed', 4: 'gbcPurple' };
      return map[salesIndex % 5] || 'gbcGrey';
    },

    getSalesColorClass(salesname, eventDate) {
      if (eventDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const evDate = new Date(eventDate);
        evDate.setHours(0, 0, 0, 0);
        if (evDate < today) return 'gbcGrey';
      }
      const salesIndex = this.salesList.findIndex(s => s.empName === salesname);
      const map = { 0: 'gbcGreen', 1: 'gbcOrange', 2: 'gbcBlue', 3: 'gbcRed', 4: 'gbcPurple' };
      return map[salesIndex % 5] || 'gbcGrey';
    },

    getVisibleEvents(date) {
      return this.getDateEvents(date).slice(0, 6);
    },

    getHiddenEventsCount(date) {
      return Math.max(0, this.getDateEvents(date).length - 6);
    },

    saveSchedule() {
      const userInfo = this.$store.getters.userInfo;
      this.scheduleForm.sales = userInfo.UserName;

      if (!this.scheduleForm.date || !this.scheduleForm.target || !this.scheduleForm.startTime || !this.scheduleForm.endTime) {
        this.$notify({ title: '錯誤', message: '請填寫時間及事由必填欄位', type: 'error' });
        return;
      }

      const startHour = parseInt(this.scheduleForm.startTime.split(':')[0]);
      const endHour   = parseInt(this.scheduleForm.endTime.split(':')[0]);
      if (startHour >= endHour) {
        this.$notify({ title: '錯誤', message: '結束時間必須比開始時間晚', type: 'error' });
        return;
      }

      if (this.currentEditIndex !== -1) {
        const eventToUpdate = this.dayEvents[this.currentEditIndex];
        const updateData = {
          ID:        eventToUpdate.id,
          Date:      this.scheduleForm.date,
          StartTime: this.scheduleForm.startTime,
          EndTime:   this.scheduleForm.endTime,
          Title:     this.scheduleForm.target,
          Memo:      this.scheduleForm.Memo || '',
        };
        this.$api.apicalendar.calendar_update(updateData).then(res => {
          if (res.statuscode == 200) {
            notify({ type: 'success', message: '行程更新成功' });
            this.getList();
            this.currentEditIndex = -1;
          } else {
            notify({ type: 'error', message: '更新行程失敗，請稍後再試' });
          }
        }).catch(() => {
          notify({ type: 'error', message: '更新行程失敗，請稍後再試' });
        });
      } else {
        const createData = {
          Date:      this.scheduleForm.date,
          StartTime: this.scheduleForm.startTime,
          EndTime:   this.scheduleForm.endTime,
          Title:     this.scheduleForm.target,
          Memo:      this.scheduleForm.Memo || '',
        };
        this.$api.apicalendar.calendar_create(createData).then(res => {
          if (res.statuscode == 200) {
            notify({ type: 'success', message: '新增行程成功' });
            this.getList();
          } else {
            notify({ type: 'error', message: '新增行程失敗，請稍後再試' });
          }
        }).catch(() => {
          notify({ type: 'error', message: '新增行程失敗，請稍後再試' });
        });
      }

      this.updateEventColors();
      this.drawerVisible = false;
    },

    updateEventColors() {
      this.calendarData.forEach(event => {
        event.bgcolor = this.getSalesColorClass(event.sales, event.date);
      });
    },

    doSearch() {
      this.getList();
    },

    getList() {
      this.$store.dispatch('loadingHandler', true);
      let dateStart, dateEnd;
      if (this.selyyyyMM) {
        const [year, month] = this.selyyyyMM.split('-');
        dateStart = `${year}-${month}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = new Date(year, month, lastDay + 1);
        dateEnd = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      } else {
        const today = new Date();
        const year  = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        dateStart = `${year}-${month}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = new Date(year, month, lastDay + 1);
        dateEnd = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      }

      const qry = { DateStart: dateStart, DateEnd: dateEnd };
      if (this.listQuery.DeptID) qry.DeptID = this.listQuery.DeptID;
      if (this.selSales && this.selSales !== '全部') qry.UserID = this.selSales;

      this.$api.apicalendar.calendar_search(qry).then(res => {
        this.calendarData = res.data;
        // 偵錯：印出案件類型事件的欄位
        const caseItem = res.data.find(i => i.source === 'case');
        if (caseItem) console.log('[CalMgmt] case event fields:', JSON.stringify(caseItem));
        const uniqueSalesMap = new Map();
        this.calendarData.forEach(item => {
          if (item.userId && !uniqueSalesMap.has(item.userId)) uniqueSalesMap.set(item.userId, item);
        });
        this.salesList = Array.from(uniqueSalesMap.values()).map(item => ({
          empID:   item.userId,
          empName: item.sales,
        }));
      }).catch(() => {
        notify({ type: 'error', message: '取得行事曆失敗，請稍後再試' });
      }).finally(() => {
        this.$store.dispatch('loadingHandler', false);
      });
    },

    getBranchStaff(deptID) {
      const qry = { DeptID: deptID, isSys: true };
      this.$api.apidivison.contact_list_search(qry).then(response => {
        this.branchStaffList = response.data.filter(item => item.RoleName == "UC" || item.RoleName == "UAO")
        .map(item => ({
          ID:       item.ID,
          UserID:   item.UserID,
          UserName: item.UserName,
        }));
      }).catch(() => {
        notify({ title: '錯誤', message: '獲取分行承辦人清單失敗，請稍後再試', type: 'error' });
      });
    },
 
    async getFunctions() {
      try {
        const funs = this.$store.getters.userInfo;
        let RoleName   = [];
        let DivisionCode = '';
        if (funs && funs.Role) {
          RoleName     = funs.Role;
          DivisionCode = funs.DivisionCode;
          const allowedRoles = ['HE', 'HC1', 'HD', 'HC'];
          this.divBtnVisible = RoleName.some(r => allowedRoles.includes(r));
        } else {
          notify({ title: '錯誤', message: '無法取得使用者功能列表，請重新登入', type: 'error' });
        }

        this.btnCalendarEditable = !this.selectedDiv;
        if (RoleName.includes('UAO') || RoleName.includes('UC')) {
          this.btnCalendarEditable = true;
        }
        if (RoleName.includes('HE') || RoleName.includes('HC1')) {
          this.filterDivisionCode = DivisionCode;
        }
      } catch {
        notify({ title: '錯誤', message: '獲取使用者功能列表失敗，請稍後再試', type: 'error' });
      }
    },
  },

  async mounted() {
    this.$store.dispatch('loadingHandler', true);
    // 移除 el-calendar 內建 header（月份切換由搜尋列控制）
    const calHeader = this.$el.querySelector('div.el-calendar__header');
    if (calHeader) calHeader.remove();

    await this.getFunctions();
    if (!this.divBtnVisible) {
      this.updateEventColors();
      const deptID = window.localStorage.getItem('deptID');
      this.getBranchStaff(deptID);
      this.getList();
    } else {
      this.calendarData = [];
    }
    this.$store.dispatch('loadingHandler', false);
  },
};
</script>

<style lang="scss" scoped>
// ── 色票 ─────────────────────────────────────────────────────
$primary: #1c2d5e;

.gbcGreen  { background-color: #90ee90; }
.gbcOrange { background-color: #fc9;    }
.gbcBlue   { background-color: #add8e6; }
.gbcRed    { background-color: #f08080; }
.gbcPurple { background-color: #9c27b0; color: #fff; }
.gbcGrey   { background-color: #9e9e9e; color: #fff; }

// ── 根容器 ───────────────────────────────────────────────────
#mCalMgmtBox {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: #f4f6fb;
  position: relative;
}

// ── 搜尋列 ───────────────────────────────────────────────────
.m-search-bar {
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 4px;
}

.m-search-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  font-weight: 600;
  color: $primary;
  font-size: 15px;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.m-search-body {
  padding: 0 16px 16px;

  .m-form-row {
    margin-bottom: 12px;

    label {
      display: block;
      font-weight: 600;
      font-size: 13px;
      color: #555;
      margin-bottom: 4px;
    }
  }
}

// ── 圖例滾動列 ───────────────────────────────────────────────
.m-legend-scroll {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 6px 12px;
  gap: 8px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;

  &::-webkit-scrollbar { display: none; }
}

.m-legend-item {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: bold;
  color: #333;
  padding: 4px 10px;
  border-radius: 4px;
}

// ── 行事曆容器 ───────────────────────────────────────────────
.m-calendar-wrap {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 4px 80px;   // 底部留 FAB 空間

  // 覆蓋 el-calendar 預設樣式
  ::v-deep .el-calendar-table td {
    border: 1px solid #e8e8e8;
  }

  ::v-deep .el-calendar-table .el-calendar-day {
    padding: 2px;
    height: auto;
    min-height: 70px;
  }

  ::v-deep .el-calendar__body {
    padding: 4px;
  }
}

// ── 日曆格子 ─────────────────────────────────────────────────
.m-cd {
  display: flex;
  flex-direction: column;
  min-height: 68px;
  padding: 2px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.m-date-num {
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  line-height: 1.6;

  &.is-selected {
    background: #1c2d5e;
    color: #fff;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    line-height: 22px;
    margin: 0 auto;
    text-align: center;
  }
}

.m-event-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  flex: 1;
}

.m-ev-group {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 1px 3px;
  border-radius: 3px;
  width: 100%;
}

.m-ev-label {
  display: block;
  font-size: 10px;
  font-weight: bold;
  color: #333;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.m-more-badge {
  font-size: 10px;
  color: #666;
  background: #f0f0f0;
  border-radius: 3px;
  text-align: center;
  padding: 0 2px;
}

// ── FAB ──────────────────────────────────────────────────────
.m-fab {
  position: fixed;
  right: 20px;
  bottom: 84px;   // 避開 MobileHome TabBar (64px) + 間距
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: $primary;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  z-index: 200;
  -webkit-tap-highlight-color: transparent;

  i {
    font-size: 24px;
  }

  &:active {
    opacity: 0.8;
    transform: scale(0.95);
  }
}

// ── 抽屜共用 ─────────────────────────────────────────────────
.m-drawer-content {
  padding: 16px 16px 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.m-form-item {
  margin-bottom: 16px;

  label {
    display: block;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 6px;

    .req {
      color: #f56c6c;
      margin-left: 2px;
    }
  }
}

.m-drawer-footer {
  display: flex;
  gap: 10px;
  padding: 16px 0;
  margin-top: auto;
  border-top: 1px solid #eee;
}

.m-drawer-add-btn {
  padding: 12px 0 4px;
  border-top: 1px solid #eee;
  margin-top: 12px;
}

// ── 事件列表抽屜 ─────────────────────────────────────────────
.m-no-events {
  text-align: center;
  color: #999;
  padding: 32px 0;
  font-size: 14px;
}

.m-events-list {
  flex: 1;
  overflow-y: auto;
}

.m-event-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid transparent;
  background: #f8f9fc;
  margin-bottom: 10px;

  &.gbcGreen  { border-left-color: #67c23a; }
  &.gbcOrange { border-left-color: #e6a23c; }
  &.gbcBlue   { border-left-color: #409eff; }
  &.gbcRed    { border-left-color: #f56c6c; }
  &.gbcPurple { border-left-color: #9c27b0; }
  &.gbcGrey   { border-left-color: #9e9e9e; }
}

.m-event-body {
  flex: 1;
  min-width: 0;
}

.m-event-title {
  font-weight: bold;
  font-size: 15px;
  margin-bottom: 4px;
  word-break: break-word;
}

.m-event-meta {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.m-event-memo {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.m-event-tags {
  margin-top: 6px;

  .el-tag {
    margin-right: 4px;
  }
}

.m-event-btns {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 8px;

  ::v-deep .el-button.is-disabled,
  ::v-deep .el-button.is-disabled:hover,
  ::v-deep .el-button.is-disabled:focus {
    pointer-events: auto;
    cursor: not-allowed !important;
    opacity: 0.45;
    filter: grayscale(20%);
    box-shadow: none;
  }
}

.m-lock-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #909399;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
}

// ── 案件 dialog ──────────────────────────────────────────────
.m-dialog-box {
  padding: 0 4px;
}

.m-detail-row {
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  border-bottom: 1px solid #ebeef5;
}

.m-detail-label {
  font-weight: bold;
  font-size: 13px;
  color: #303133;
  margin-bottom: 6px;
}

.m-detail-value {
  font-size: 14px;
  color: #606266;
  word-break: break-word;
}
</style>
